import { useState, useEffect, useCallback } from 'react'
import { r2Api, backupsApi } from '../api'
import {
  HiCloudArrowUp, HiCheckCircle, HiXCircle, HiTrash,
  HiArrowDownTray, HiArrowPath, HiInformationCircle
} from 'react-icons/hi2'
import { Spinner } from '../hooks/useApi'
import { Beacon, ConfirmModal, Loader } from '../ui'
import { useStore } from '../store'

const inputStyle = {
  width: '100%', padding: '8px 12px', borderRadius: 6,
  border: '1px solid var(--ui-border)', fontSize: 13,
  transition: 'border-color var(--ui-dur) var(--ui-ease)', background: 'var(--ui-surface)'
}
const labelStyle = { fontSize: 12, fontWeight: 600, color: 'var(--ui-text-2)', marginBottom: 6, display: 'block' }
const rowStyle = { display: 'flex', flexDirection: 'column', gap: 4 }

function fmtDate(iso) {
  if (!iso) return '—'
  try { return new Date(iso).toLocaleString('uz-UZ') } catch { return iso }
}

export default function R2SettingsCard() {
  const { addToast } = useStore()
  const [cfg, setCfg] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(false)

  // maxfiy maydonlar — faqat kiritilganda yuboriladi
  const [accessKey, setAccessKey] = useState('')
  const [secretKey, setSecretKey] = useState('')

  // R2 dagi nusxalar
  const [remote, setRemote] = useState([])
  const [remoteLoading, setRemoteLoading] = useState(false)
  const [remoteErr, setRemoteErr] = useState(null)
  const [busyKey, setBusyKey] = useState(null)
  // R2 dan o'chirish tasdig'i kutilayotgan kalit
  const [pendingDelete, setPendingDelete] = useState(null)

  // lokal nusxalar (qo'lda yuklash uchun)
  const [local, setLocal] = useState([])

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try { setCfg(await r2Api.getConfig()) }
    catch (e) { setError(e.message) }
    finally { setLoading(false) }
  }, [])

  const loadRemote = useCallback(async () => {
    setRemoteLoading(true); setRemoteErr(null)
    try {
      const res = await r2Api.list()
      if (res.ok) setRemote(res.items || [])
      else { setRemote([]); setRemoteErr(res.error) }
    } catch (e) { setRemoteErr(e.message) }
    finally { setRemoteLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    if (cfg?.configured) {
      loadRemote()
      backupsApi.list().then(setLocal).catch(() => {})
    }
  }, [cfg?.configured, loadRemote])

  const upd = (k, v) => setCfg(p => ({ ...p, [k]: v }))

  const handleSave = async () => {
    setSaving(true); setError(null); setSaved(false)
    try {
      const body = {
        enabled: cfg.enabled,
        auto_upload: cfg.auto_upload,
        account_id: cfg.account_id,
        bucket: cfg.bucket,
        prefix: cfg.prefix,
        retention: cfg.retention,
      }
      if (accessKey.trim()) body.access_key = accessKey.trim()
      if (secretKey.trim()) body.secret_key = secretKey.trim()
      const updated = await r2Api.updateConfig(body)
      setCfg(updated); setAccessKey(''); setSecretKey('')
      setSaved(true); setTimeout(() => setSaved(false), 2500)
      if (updated.configured) loadRemote()
    } catch (e) { setError(e.message) }
    finally { setSaving(false) }
  }

  const handleTest = async () => {
    setTesting(true); setTestResult(null)
    try { setTestResult(await r2Api.test()) }
    catch (e) { setTestResult({ ok: false, error: e.message }) }
    finally { setTesting(false) }
  }

  const handleUploadLocal = async (filename) => {
    setBusyKey(filename)
    try {
      await r2Api.upload(filename)
      await loadRemote()
    } catch (e) { addToast('Yuklashda xato: ' + (e.message || 'server javob bermadi'), 'critical') }
    finally { setBusyKey(null) }
  }

  const handlePull = async (key) => {
    setBusyKey(key)
    try {
      const res = await r2Api.pull(key)
      addToast(`Tushirildi: ${res.filename} — «Zahira nusxalari» ro'yxatidan tiklash mumkin`, 'ok')
      backupsApi.list().then(setLocal).catch(() => {})
    } catch (e) { addToast('Yuklab olishda xato: ' + (e.message || 'server javob bermadi'), 'critical') }
    finally { setBusyKey(null) }
  }

  const handleDeleteRemote = async (key) => {
    setPendingDelete(null)
    setBusyKey(key)
    try { await r2Api.remove(key); await loadRemote() }
    catch (e) { addToast("O'chirishda xato: " + (e.message || 'server javob bermadi'), 'critical') }
    finally { setBusyKey(null) }
  }

  if (loading) return <div className="card"><div className="card-body"><Loader state="working" block /></div></div>
  if (!cfg) return <div className="card"><div className="card-body">Sozlamalarni yuklab bo'lmadi</div></div>

  const localNotInR2 = local.filter(b => !remote.some(r => r.filename === b.filename))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── Sozlamalar ── */}
      <div className="card">
        <div className="card-hdr" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <HiCloudArrowUp size={20} style={{ color: 'var(--ui-text-1)' }} />
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0 }}>Offsite zahira — Cloudflare R2</h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--ui-text-2)' }}>
              Zahira nusxalari serverdan tashqarida saqlanadi — server buzilsa ham ma'lumot omon qoladi
            </p>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={!!cfg.enabled} onChange={e => upd('enabled', e.target.checked)} />
            Yoqilgan
          </label>
        </div>

        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {error && <div style={{ background: 'var(--ui-st-critical-bg)', border: '1px solid var(--ui-st-critical)', borderRadius: 6, padding: '10px 12px', fontSize: 13, color: 'var(--ui-st-critical)' }}>{error}</div>}
          {saved && <div style={{ background: 'var(--ui-st-ok-bg)', border: '1px solid var(--ui-st-ok)', borderRadius: 6, padding: '10px 12px', fontSize: 13, color: 'var(--ui-st-ok)' }}>Saqlandi!</div>}
          {!cfg.enabled && cfg.access_key_set && cfg.secret_key_set && cfg.account_id && cfg.bucket && (
            <div style={{ background: 'var(--ui-st-medium-bg)', border: '1px solid var(--ui-st-medium)', borderRadius: 6, padding: '10px 12px', fontSize: 13, color: 'var(--ui-st-medium)', display: 'flex', gap: 8 }}>
              <Beacon size={16} tone="inherit" style={{ flexShrink: 0, marginTop: 1 }} aria-hidden="true" />
              <span>Sozlamalar to'liq, lekin <b>«Yoqilgan» belgilanmagan</b> — zaxiralar R2 ga <b>yuklanmaydi</b>. Yuqoridagi «Yoqilgan» ni belgilab, <b>«Saqlash»</b> bosing.</span>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div style={rowStyle}>
              <label style={labelStyle}>Account ID</label>
              <input style={inputStyle} value={cfg.account_id || ''} placeholder="masalan: a1b2c3d4e5f6..."
                     onChange={e => upd('account_id', e.target.value)} />
            </div>
            <div style={rowStyle}>
              <label style={labelStyle}>Bucket nomi</label>
              <input style={inputStyle} value={cfg.bucket || ''} placeholder="crt-backups"
                     onChange={e => upd('bucket', e.target.value)} />
            </div>
            <div style={rowStyle}>
              <label style={labelStyle}>
                Access Key ID {cfg.access_key_set && (
                  <span style={{ color: 'var(--ui-st-ok)' }}>
                    <HiCheckCircle size={12} style={{ verticalAlign: -2 }} aria-hidden="true" /> o'rnatilgan ({cfg.access_key_masked})
                  </span>
                )}
              </label>
              <input style={inputStyle} type="password" value={accessKey} autoComplete="new-password"
                     placeholder={cfg.access_key_set ? "O'zgartirish uchun yangisini kiriting" : 'Access Key ID'}
                     onChange={e => setAccessKey(e.target.value)} />
            </div>
            <div style={rowStyle}>
              <label style={labelStyle}>
                Secret Access Key {cfg.secret_key_set && (
                  <span style={{ color: 'var(--ui-st-ok)' }}>
                    <HiCheckCircle size={12} style={{ verticalAlign: -2 }} aria-hidden="true" /> o'rnatilgan ({cfg.secret_key_masked})
                  </span>
                )}
              </label>
              <input style={inputStyle} type="password" value={secretKey} autoComplete="new-password"
                     placeholder={cfg.secret_key_set ? "O'zgartirish uchun yangisini kiriting" : 'Secret Access Key'}
                     onChange={e => setSecretKey(e.target.value)} />
            </div>
            <div style={rowStyle}>
              <label style={labelStyle}>Papka (prefiks)</label>
              <input style={inputStyle} value={cfg.prefix || ''} placeholder="crt-backups/"
                     onChange={e => upd('prefix', e.target.value)} />
            </div>
            <div style={rowStyle}>
              <label style={labelStyle}>R2 da nechta nusxa saqlansin (0 = cheksiz)</label>
              <input style={inputStyle} type="number" min={0} value={cfg.retention ?? 30}
                     onChange={e => upd('retention', e.target.value)} />
            </div>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
            <input type="checkbox" checked={!!cfg.auto_upload} onChange={e => upd('auto_upload', e.target.checked)} />
            Avtomatik yuklash — har bir zahira yaratilgandan so'ng R2 ga jo'natilsin
          </label>

          <div style={{
            background: 'var(--ui-surface-2)', border: '1px solid var(--ui-border)',
            borderRadius: 6, padding: '10px 12px', fontSize: 12, color: 'var(--ui-text-2)',
            display: 'flex', gap: 8
          }}>
            <HiInformationCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} aria-hidden="true" />
            <span>
              Cloudflare panelida: <b>R2 → Manage API Tokens → Create API Token</b>, huquq <b>Object Read &amp; Write</b>.
              Account ID R2 sahifasining o'ng tomonida ko'rinadi. Kalitlar bazada shifrlangan holda saqlanadi.
            </span>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <Spinner size={14} /> : 'Saqlash'}
            </button>
            <button className="btn" onClick={handleTest} disabled={testing || !cfg.enabled}>
              {testing ? <Spinner size={14} /> : 'Ulanishni tekshirish'}
            </button>
            {testResult && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13,
                color: testResult.ok ? 'var(--ui-st-ok)' : 'var(--ui-st-critical)'
              }}>
                {testResult.ok ? <HiCheckCircle size={16} /> : <HiXCircle size={16} />}
                {testResult.ok ? testResult.message : testResult.error}
              </span>
            )}
          </div>

          {(cfg.last_sync_at || cfg.last_status) && (
            <div style={{ fontSize: 12, color: 'var(--ui-text-2)' }}>
              Oxirgi sinxronizatsiya: <b>{fmtDate(cfg.last_sync_at)}</b>
              {cfg.last_status && <> — <span style={{ color: cfg.last_status.startsWith('OK') ? 'var(--ui-st-ok)' : 'var(--ui-st-critical)' }}>{cfg.last_status}</span></>}
            </div>
          )}
        </div>
      </div>

      {/* ── R2 dagi nusxalar ── */}
      {cfg.configured && (
        <div className="card">
          <div className="card-hdr" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h3 style={{ margin: 0, flex: 1 }}>R2 dagi nusxalar {remote.length > 0 && `(${remote.length})`}</h3>
            <button className="btn btn-sm" onClick={loadRemote} disabled={remoteLoading}>
              {remoteLoading ? <Spinner size={12} /> : <HiArrowPath size={14} />} Yangilash
            </button>
          </div>
          <div className="card-body">
            {remoteErr && <div style={{ color: 'var(--ui-st-critical)', fontSize: 13, marginBottom: 12 }}>{remoteErr}</div>}

            {localNotInR2.length > 0 && (
              <div style={{ marginBottom: 16, padding: '10px 12px', background: 'var(--ui-surface-2)', border: '1px solid var(--ui-border)', borderRadius: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                  R2 ga yuklanmagan lokal nusxalar ({localNotInR2.length}):
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {localNotInR2.slice(0, 8).map(b => (
                    <button key={b.filename} className="btn btn-sm" disabled={busyKey === b.filename}
                            onClick={() => handleUploadLocal(b.filename)}>
                      {busyKey === b.filename ? <Spinner size={12} /> : <HiCloudArrowUp size={14} />} {b.filename}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {remoteLoading ? <Loader state="searching" /> : remote.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--ui-text-2)', padding: '12px 0' }}>
                R2 da hali nusxa yo'q. Zahira yaratganingizda avtomatik yuklanadi.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {remote.map(item => (
                  <div key={item.key} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                    border: '1px solid var(--ui-border)', borderRadius: 6, fontSize: 13
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.filename}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--ui-text-2)' }}>
                        {item.size_mb} MB · {fmtDate(item.modified)}
                      </div>
                    </div>
                    <button className="btn btn-sm" disabled={busyKey === item.key}
                            title="Serverga tushirish (keyin tiklash mumkin)"
                            aria-label={`Serverga tushirish: ${item.filename}`}
                            onClick={() => handlePull(item.key)}>
                      {busyKey === item.key ? <Spinner size={12} /> : <HiArrowDownTray size={14} />}
                    </button>
                    <button className="btn btn-sm btn-danger" disabled={busyKey === item.key}
                            title="R2 dan o'chirish"
                            aria-label={`R2 dan o'chirish: ${item.filename}`}
                            onClick={() => setPendingDelete(item.key)}>
                      <HiTrash size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => handleDeleteRemote(pendingDelete)}
        title="Zahira nusxasi R2 dan o'chiriladi"
        text="Nusxa Cloudflare R2 dan butunlay o'chadi. Uni qaytarib bo'lmaydi."
        confirmLabel="O'chirish"
        danger
      >
        <div style={{
          marginTop: 8, padding: '8px 12px', background: 'var(--ui-surface-2)',
          border: '1px solid var(--ui-border)', borderRadius: 6,
          fontFamily: 'var(--ui-mono)', fontSize: 12, wordBreak: 'break-all'
        }}>
          {pendingDelete}
        </div>
      </ConfirmModal>
    </div>
  )
}
